import { useEffect } from "react";
import { Recycle, ShieldCheck, FileText, Leaf } from "lucide-react";
import { MaskedLine, Overline, Reveal } from "@/components/Reveal";
import VesselFrame from "@/components/VesselFrame";
import { CONTACT } from "@/components/ContactCards";

const IMG = {
  hero: "https://images.unsplash.com/photo-1772870222730-8a69ebd936d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxzaGlwJTIwZGVtb2xpdGlvbiUyMHJlY3ljbGluZyUyMHNoaXBicmVha2luZyUyMHlhcmR8ZW58MHx8fHwxNzkwNDEzMzY1fDA&ixlib=rb-4.1.0&q=85",
  yard: "https://images.unsplash.com/photo-1760432787792-2d91519cb66e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzaGlwJTIwZGVtb2xpdGlvbiUyMHJlY3ljbGluZyUyMHNoaXBicmVha2luZyUyMHlhcmR8ZW58MHx8fHwxNzkwNDEzMzY1fDA&ixlib=rb-4.1.0&q=85",
};

const COMPLIANCE = [
  { icon: ShieldCheck, title: "Hong Kong Convention (HKC)", copy: "Placements follow HKC safe and environmentally sound recycling standards, with yard certification verified before nomination." },
  { icon: Recycle, title: "EU Ship Recycling Regulation", copy: "For EU-flagged or EU-bound tonnage, we work with facilities on the European List of approved ship recycling yards." },
  { icon: FileText, title: "IHM Documentation", copy: "Inventory of Hazardous Materials preparation and verification is coordinated ahead of sale, protecting sellers from downstream liability." },
  { icon: Leaf, title: "Basel Convention Alignment", copy: "Cross-border movements of end-of-life vessels are structured to respect Basel waste-shipment requirements." },
];

const PROCESS = [
  { step: "01", title: "LDT Assessment & Valuation", copy: "We assess lightweight displacement tonnage, steel composition and outfit, then benchmark against live demolition indices across the subcontinent and Turkey." },
  { step: "02", title: "Cash Buyer & Yard Selection", copy: "Candidate cash buyers and recycling yards are shortlisted on price, payment security, HKC compliance and delivery logistics." },
  { step: "03", title: "Delivery & Beaching", copy: "Final voyage arrangements, port clearances and beaching are coordinated between owner, cash buyer and yard — with a clean handover protocol." },
  { step: "04", title: "Settlement & Title Transfer", copy: "Payment is secured against delivery, title transfers on documented terms, and deregistration is completed with the flag state." },
  { step: "05", title: "Recycling Audit & Documentation", copy: "Statement of completion, IHM closure and recycling certificates are collated into a final audit pack for the seller's records." },
];

export default function Demolition() {
  useEffect(() => {
    document.title = "Demolition & Green Ship Recycling — Dice International LLC";
  }, []);

  return (
    <div data-testid="demolition-page">
      <section className="relative overflow-hidden" data-testid="demo-hero">
        <img src={IMG.hero} alt="Vessel at a ship recycling facility" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030F26] via-[#030F26]/70 to-[#030F26]/40" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 pb-20 pt-32 sm:px-10 lg:pt-40">
          <Overline testId="demo-overline">Services — 02</Overline>
          <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="demo-heading">
            <MaskedLine delay={0.15}>Demolition &amp; Recycling,</MaskedLine>
            <MaskedLine delay={0.3}>
              <em className="text-[#C5A059]">done responsibly.</em>
            </MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="demo-intro">
              End-of-life brokerage connecting owners with vetted cash buyers and HKC-compliant green recycling
              yards — maximum value, minimum exposure.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32" data-testid="demo-compliance-section">
        <Reveal>
          <Overline testId="demo-compliance-overline">Compliance Framework</Overline>
          <h2 className="mt-6 max-w-2xl font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="demo-compliance-heading">
            Recycling that stands up to scrutiny.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="demo-compliance-grid">
          {COMPLIANCE.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08} className="h-full">
              <div className="flex h-full flex-col border border-[#1E3A6E]/60 bg-[#091B3E]/70 p-7 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1.5 hover:border-[#C5A059]/60">
                <c.icon className="h-6 w-6 text-[#C5A059]" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-lg font-semibold text-white">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-[#1E3A6E]/40 bg-[#071838]/30" data-testid="demo-process-section">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32">
          <div className="grid grid-cols-1 gap-14">
            <div className="lg:col-span-4">
              <Reveal>
                <Overline testId="demo-process-overline">The Typical Placement</Overline>
                <h2 className="mt-6 font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="demo-process-heading">
                  From LDT to final certificate.
                </h2>
                <p className="mt-6 text-base leading-relaxed text-slate-400">
                  A demolition sale carries regulatory and reputational risk. Our process is built to remove both
                  while protecting the seller's price.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <VesselFrame src={IMG.yard} alt="Green ship recycling yard" testId="demo-yard-frame" className="mt-10 h-80" />
              </Reveal>
            </div>
            <div className="lg:col-span-8" data-testid="demo-process-steps">
              {PROCESS.map((p, i) => (
                <Reveal key={p.step} delay={i * 0.06}>
                  <div className="flex gap-6 border-t border-[#1E3A6E]/50 py-8 first:border-t-0 first:pt-0 sm:gap-10">
                    <span className="font-mono text-sm text-[#C5A059]">{p.step}</span>
                    <div>
                      <h3 className="font-serif text-xl font-semibold text-white sm:text-2xl">{p.title}</h3>
                      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{p.copy}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32" data-testid="demo-enquiry-section">
        <div className="grid grid-cols-1 gap-14">
          <div className="max-w-2xl">
            <Reveal>
              <Overline testId="demo-enquiry-overline">Recycling Enquiries</Overline>
              <h2 className="mt-6 font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="demo-enquiry-heading">
                Have a vessel approaching her final voyage?
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                Send the vessel's particulars — type, built, LDT and location. We will return a market view and a
                compliant placement strategy.
              </p>
              <div className="mt-8 space-y-3 font-mono text-sm text-slate-300">
                <p>
                  <span className="text-slate-500">EMAIL&nbsp;&nbsp;</span>
                  <a data-testid="demo-email-link" href={`mailto:${CONTACT.email}`} className="text-[#C5A059] transition-colors duration-300 hover:text-[#E2C08D]">{CONTACT.email}</a>
                </p>
                <p>
                  <span className="text-slate-500">DIRECT&nbsp;</span>
                  <a data-testid="demo-phone-link" href={`tel:${CONTACT.phoneRaw}`} className="text-[#C5A059] transition-colors duration-300 hover:text-[#E2C08D]">{CONTACT.phoneDisplay}</a>
                </p>
                <p>
                  <span className="text-slate-500">WHATSAPP&nbsp;</span>
                  <a data-testid="demo-whatsapp-link" href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] transition-colors duration-300 hover:text-[#E2C08D]">{CONTACT.phoneDisplay}</a>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
